import { useRef } from "react";
import type { BannerOf } from "../runtime/bannerMatching";
import {
  type CountdownView,
  useBannerCountdown,
} from "../runtime/useBannerCountdown";
import { useDashboardBanner } from "../runtime/useDashboardBanner";
import { StickyBottomBarBanner } from "../templates/StickyBottomBarBanner";
import { BOTTOM_BAR_INSET, usePublishedHeight } from "./bottomInsets";
import { countdownContent } from "./countdownContent";
import { iconVisualOf } from "./bannerIcons";
import { PageFrame } from "./PageFrame";

/**
 * The Sticky bottom bar banner the runtime picks for this page, kept at the
 * bottom of the screen while the page scrolls, as wide as the page content
 * container (the Top bar's width too). It is the last thing in the page's
 * flow, pushed to the end of a short page: the page keeps its full height
 * above the bar, so nothing is behind it once the page is scrolled to its end.
 */
export const StickyBottomBarPlacement = () => {
  const { banner, followCta, close } = useDashboardBanner({
    template: "stickyBottomBar",
  });
  // Here, beside the runtime: its last tick re-picks a banner set to hide.
  const countdown = useBannerCountdown(banner);

  if (!banner) return null;

  return (
    <StickyBottomBar
      banner={banner}
      countdown={countdown}
      followCta={() => followCta(banner)}
      close={() => close(banner)}
    />
  );
};

type StickyBottomBarProps = {
  banner: BannerOf<{ template: "stickyBottomBar" }>;
  countdown: CountdownView;
  followCta: () => void;
  close: () => void;
};

const StickyBottomBar = ({
  banner,
  countdown,
  followCta,
  close,
}: StickyBottomBarProps) => {
  const placement = useRef<HTMLDivElement>(null);
  usePublishedHeight(placement, BOTTOM_BAR_INSET);
  const content = countdownContent(countdown, {
    title: banner.title,
    text: banner.text,
    ctaLabel: banner.cta.label,
  });

  return (
    <PageFrame
      ref={placement}
      data-testid="sticky-bottom-bar-placement"
      position="sticky"
      bottom={0}
      mt="auto"
      pt={{ xs: 3, md: 2 }}
      pb={{ xs: 3, md: 4 }}
      sx={(theme) => ({
        // Under the app bar and the mobile drawer's backdrop, over the page.
        zIndex: theme.zIndex.appBar - 2,
        // The gap around the bar takes no clicks, the page under it does.
        pointerEvents: "none",
        "& > *": { pointerEvents: "auto" },
      })}
    >
      <StickyBottomBarBanner
        title={content.title}
        text={content.text}
        countdown={content.countdown}
        ctaLabel={content.ctaLabel}
        onCtaClick={followCta}
        onClose={banner.closePeriod ? close : undefined}
        variant={banner.style}
        visual={iconVisualOf(banner.icon)}
      />
    </PageFrame>
  );
};
